import { useState } from "react";
import { Box, Badge, Button, Chip, Divider, IconButton, Popover, Tooltip, Typography } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import OrionToggleButton from "../../../components/OrionToggleButton";
import { colors } from "../../../theme";
import AssetsOverview from "../status/status-components/AssetsOverview";

// Same reseller/user branch distinction already used by Status/index.jsx.
const ASSET_DETAIL_PATH = window.sessionStorage.getItem("type") === "reseller"
  ? "/reseller/myC/asset-detail"
  : "/user/network-status/asset-detail";
const INVENTORY_PATH = window.sessionStorage.getItem("type") === "reseller"
  ? "/reseller/myC/status"
  : "/user/network-status/status";

const OverviewPage = ({ customerName }) => {

  if (!customerName && window.sessionStorage.getItem("type") === "reseller") {
    window.sessionStorage.setItem("selectedTab", "Dashboard");
    setTimeout(function checkCondition() {
      if (window.sessionStorage.getItem("selectedTab") === "Dashboard") {
        window.location.href = `/${window.sessionStorage.getItem("type")}/home`;
      } else {
        setTimeout(checkCondition, 500);
      }
    }, 0);
  }

  const navigate = useNavigate();

  // Keyed by field so each dimension (zone/vlan/vendor/asset_type) can only hold one
  // active value at a time, but different dimensions stack together (AND'd) — e.g.
  // Zone "Server Room" + VLAN 10 at once. Owned here (not inside AssetsOverview)
  // since the manage/view-in-inventory controls live in this page's title row.
  const [overviewFilters, setOverviewFilters] = useState({});
  const [filterAnchor, setFilterAnchor] = useState(null);
  const activeFilters = Object.values(overviewFilters);

  const handleToggleFilter = (field, value, label) => {
    setOverviewFilters((cur) => {
      const next = { ...cur };
      if (next[field] && String(next[field].value) === String(value)) {
        delete next[field];
      } else {
        next[field] = { field, operator: "equals", value, label };
      }
      return next;
    });
  };

  const handleRemoveFilter = (field) => {
    setOverviewFilters((cur) => {
      const next = { ...cur };
      delete next[field];
      return next;
    });
  };

  // Overview and Inventory are separate routes now — drill-down travels via query
  // string (this app's existing convention, e.g. scenes/security/critical/index.jsx),
  // read once on mount by Status/index.jsx to seed HostsOnlineGrid's initialFilters.
  // Both the single-shot KPI-tile drill-down and the "View in Inventory" button
  // (which carries every currently active Overview filter) share the same
  // `filters` JSON-array query param contract.
  const navigateToInventory = (filters) => {
    if (!filters || filters.length === 0) {
      navigate(INVENTORY_PATH);
      return;
    }
    const params = new URLSearchParams({ filters: JSON.stringify(filters) });
    navigate(`${INVENTORY_PATH}?${params.toString()}`);
  };

  const handleDrillDown = ({ field, operator, value }) => navigateToInventory([{ field, operator, value }]);

  // Always available — with no active filter it's just a shortcut to Inventory.
  const handleViewInInventory = () =>
    navigateToInventory(activeFilters.map(({ field, operator, value }) => ({ field, operator, value })));

  const handleOpenAsset = (assetId) => {
    navigate(`${ASSET_DETAIL_PATH}?assetId=${encodeURIComponent(assetId)}`);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={1.5}>
          <Header title="Overview" subtitle=" " />
          <OrionToggleButton />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Manage filters">
            <IconButton onClick={(e) => setFilterAnchor(e.currentTarget)}>
              <Badge badgeContent={activeFilters.length} color="primary">
                <FilterAltIcon sx={{ color: activeFilters.length > 0 ? "#459095" : colors.grey[400] }} />
              </Badge>
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="outlined"
            startIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
            onClick={handleViewInInventory}
            sx={{ textTransform: "none", fontSize: 12.5 }}
          >
            View in Inventory
          </Button>
        </Box>
      </Box>

      <Popover
        open={!!filterAnchor}
        anchorEl={filterAnchor}
        onClose={() => setFilterAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#161616",
              backgroundImage: "none",
              border: `1px solid ${colors.grey[800]}`,
              borderRadius: 1.5,
              mt: 0.5,
            },
          },
        }}
      >
        <Box sx={{ p: 1.75, minWidth: 260 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.2 }}>
            <FilterAltIcon sx={{ color: "#459095", fontSize: 17 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: colors.grey[100] }}>
              Active Filters
            </Typography>
            {activeFilters.length > 0 && (
              <Chip
                label={activeFilters.length}
                size="small"
                sx={{ ml: "auto", bgcolor: "#45909522", color: "#459095", fontSize: 11, fontWeight: 700, height: 20 }}
              />
            )}
          </Box>
          {activeFilters.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: colors.grey[500], fontStyle: "italic", maxWidth: 220 }}>
              None yet — click a Type/Vendor/VLAN/Zone breakdown below to filter this Overview by it.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}>
              {activeFilters.map((f) => (
                <Chip
                  key={f.field}
                  label={f.label}
                  size="small"
                  onDelete={() => handleRemoveFilter(f.field)}
                  sx={{
                    justifyContent: "space-between",
                    bgcolor: "#45909522", color: "#459095", fontWeight: 600,
                    "& .MuiChip-deleteIcon": { color: "#459095aa", "&:hover": { color: "#459095" } },
                  }}
                />
              ))}
              <Divider sx={{ my: 0.5, borderColor: colors.grey[800] }} />
              <Button size="small" onClick={() => setOverviewFilters({})} sx={{ color: colors.grey[400], textTransform: "none", fontSize: 12 }}>
                Clear all
              </Button>
            </Box>
          )}
        </Box>
      </Popover>

      <AssetsOverview
        overviewFilters={overviewFilters}
        onToggleFilter={handleToggleFilter}
        onDrillDown={handleDrillDown}
        onOpenAsset={handleOpenAsset}
      />
    </Box>
  );
};

export default OverviewPage;
